import { useCallback, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import Config from "../../../Config";
import { useI18n } from "../../../i18n/I18nProvider";

export const useSchedulePersonalEvents = ({ timeZone, loadData, setModalSuccessText, setModalAlertText, setModalErrorText }) => {
  const { t } = useI18n();
  const [isPersonalTaskModalOpen, setIsPersonalTaskModalOpen] = useState(false);
  const [personalTaskForModal, setPersonalTaskForModal] = useState(null);
  const [isConfirmDeleteTaskOpen, setIsConfirmDeleteTaskOpen] = useState(false);
  const [pendingDeleteTask, setPendingDeleteTask] = useState(null);
  const [isDeletingPersonalTask, setIsDeletingPersonalTask] = useState(false);

  const openCreatePersonalTaskModal = useCallback(() => {
    setPersonalTaskForModal(null);
    setIsPersonalTaskModalOpen(true);
  }, []);

  const openEditPersonalTaskModal = useCallback((task) => {
    setPersonalTaskForModal(task);
    setIsPersonalTaskModalOpen(true);
  }, []);

  const closePersonalTaskModal = useCallback(() => {
    setPersonalTaskForModal(null);
    setIsPersonalTaskModalOpen(false);
  }, []);

  const savePersonalTask = useCallback(
    async (payload, eventId) => {
      try {
        if (eventId) {
          await axios.patch(
            Config.BACKEND_ADDR + `/personal-events/${eventId}`,
            payload,
          );
        } else {
          await axios.post(Config.BACKEND_ADDR + "/personal-events/", payload);
        }
        await loadData();
        setModalSuccessText(
          t(eventId ? "Личное событие обновлено" : "Личное событие создано"),
        );
        return true;
      } catch (error) {
        if (error.response?.status === 409) {
          const detailDate = error.response?.data?.detail;
          const detailText = detailDate
            ? moment.utc(detailDate).tz(timeZone).format("DD.MM.YYYY HH:mm")
            : "";
          setModalAlertText(
            detailText
              ? t("В это время уже есть событие ({{date}}). Выберите другой слот.", { date: detailText })
              : t("В это время уже есть событие. Выберите другой слот."),
          );
        } else {
          setModalErrorText(t("Не удалось сохранить личное событие"));
        }
        return false;
      }
    },
    [timeZone, loadData, setModalSuccessText, setModalAlertText, setModalErrorText, t],
  );

  const deletePersonalTask = useCallback((task) => {
    setPendingDeleteTask(task);
    setIsDeletingPersonalTask(false);
    setIsConfirmDeleteTaskOpen(true);
  }, []);

  const handleConfirmDeletePersonalTask = useCallback(
    async (isConfirmed) => {
      if (!isConfirmed || !pendingDeleteTask || isDeletingPersonalTask) {
        setIsConfirmDeleteTaskOpen(false);
        setPendingDeleteTask(null);
        setIsDeletingPersonalTask(false);
        return;
      }
      setIsDeletingPersonalTask(true);
      try {
        await axios.delete(
          Config.BACKEND_ADDR +
            `/personal-events/${pendingDeleteTask.personalEventId}`,
        );
        await loadData();
        setModalSuccessText(t("Личное событие удалено"));
        setIsConfirmDeleteTaskOpen(false);
      } catch (error) {
        setModalErrorText(t("Не удалось удалить личное событие"));
        setIsConfirmDeleteTaskOpen(false);
      } finally {
        setPendingDeleteTask(null);
        setIsDeletingPersonalTask(false);
      }
    },
    [
      pendingDeleteTask,
      isDeletingPersonalTask,
      loadData,
      setModalSuccessText,
      setModalErrorText,
      t,
    ],
  );

  return {
    isPersonalTaskModalOpen,
    personalTaskForModal,
    isConfirmDeleteTaskOpen,
    isDeletingPersonalTask,
    openCreatePersonalTaskModal,
    openEditPersonalTaskModal,
    closePersonalTaskModal,
    savePersonalTask,
    deletePersonalTask,
    handleConfirmDeletePersonalTask,
  };
};
